/**
 * Tenant Portal – Terms of Use (read-only).
 * Linked from tenant login; no signup/accept flow.
 */

import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const Container = styled.div`
  min-height: 100vh;
  background: ${(p) => p.theme.background};
  color: ${(p) => p.theme.foreground};
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
  position: relative;
`;

const VersionBadge = styled.div`
  position: absolute;
  top: 2rem;
  right: 2rem;
  font-size: 0.75rem;
  color: ${(p) => p.theme.mutedForeground};
`;

const Header = styled.div`
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${(p) => p.theme.border};
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  color: ${(p) => p.theme.foreground};
`;

const LastUpdated = styled.p`
  font-size: 0.875rem;
  color: ${(p) => p.theme.mutedForeground};
  margin: 0;
`;

const BackLink = styled(Link)`
  display: inline-block;
  margin-bottom: 1rem;
  color: ${(p) => p.theme.primary};
  text-decoration: none;
  font-size: 0.875rem;
`;

const Content = styled.div`
  font-size: 1rem;
  color: ${(p) => p.theme.foreground};
  h2 { font-size: 1.5rem; font-weight: 600; margin: 2rem 0 1rem 0; }
  p { margin: 1rem 0; }
  ul { margin: 1rem 0; padding-left: 1.5rem; }
  li { margin: 0.5rem 0; }
`;

const FooterLinks = styled.div`
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid ${(p) => p.theme.border};
  font-size: 0.875rem;
`;

const TenantTermsOfUse: React.FC = () => {
  return (
    <Container>
      <VersionBadge>v1.0</VersionBadge>
      <BackLink to="/tenant/login">← Back to Tenant sign in</BackLink>
      <Header>
        <Title>Terms of Use – Tenant Portal</Title>
        <LastUpdated>Last Updated: 05/01/2026</LastUpdated>
      </Header>
      <Content>
        <p>
          These Terms of Use ("Terms") govern your access to and use of TenantPlus as a tenant—including the tenant portal and any tenant-facing services—operated by Circle Plus Technologies ("Circle Plus", "we", "us", "our"). By creating an account or using TenantPlus as a tenant, you agree to these Terms.
        </p>

        <h2>1. The Platform</h2>
        <p>
          TenantPlus provides software tools for property owners and managers to manage properties and tenants, and separate services and tools for tenants. We provide technology only and are not a party to any lease, rental, or property agreement between you and your landlord.
        </p>

        <h2>2. Your Account & Portal Access</h2>
        <p>
          Your tenant access is provided through a tenant portal or tenant account. Access may be granted, limited, or revoked by your landlord at any time. As a tenant you may:
        </p>
        <ul>
          <li>View your own payment information</li>
          <li>Submit maintenance or service requests</li>
          <li>Communicate with your landlord through the Platform</li>
          <li>View and sign your lease where made available</li>
        </ul>
        <p>
          We do not guarantee the accuracy of data provided by landlords or the resolution of tenant requests.
        </p>

        <h2>3. Tenant Services & Relationship with the Platform</h2>
        <p>
          TenantPlus may offer services directly to you as a tenant, independently of your landlord. Those services do not make the Platform an agent or representative of your landlord. Tenant services do not replace, modify, or enforce your lease agreement. You acknowledge that tenant services are separate from your landlord's obligations and do not create rights against your landlord.
        </p>

        <h2>4. No Involvement in Landlord–Tenant Relationships</h2>
        <p>
          We are not a party to any lease, rental agreement, or dispute between you and your landlord. We do not collect rent as a trustee, enforce lease terms, resolve disputes, or guarantee payments or outcomes. All legal and contractual obligations remain solely between you and your landlord.
        </p>

        <h2>5. Messaging & Communications</h2>
        <p>
          By using the Platform, you consent to receive service-related communications via email, SMS, WhatsApp, or similar channels (e.g. payment reminders, lease updates). Message frequency may vary, and you may manage communication preferences where available.
        </p>

        <h2>6. Payments & Fees</h2>
        <p>
          Certain features or services may require payment. Fees, billing terms, and any subscription details will be disclosed before purchase. Unless stated otherwise, payments are non-refundable.
        </p>

        <h2>7. Acceptable Use</h2>
        <p>You may not:</p>
        <ul>
          <li>Misuse the Platform</li>
          <li>Access data you are not authorized to view</li>
          <li>Interfere with system performance</li>
          <li>Use the Platform for unlawful purposes</li>
        </ul>
        <p>We reserve the right to suspend or terminate your account for violations.</p>

        <h2>8. Intellectual Property</h2>
        <p>
          The Platform, software, and all related content are owned by Circle Plus Technologies and operate under the TenantPlus brand. You are granted a limited, non-transferable right to use the Platform as intended.
        </p>

        <h2>9. Disclaimers</h2>
        <p>
          The Platform is provided "as is" without warranties of any kind. We do not guarantee uninterrupted service, data accuracy, or specific results.
        </p>

        <h2>10. Limitation of Liability</h2>
        <p>
          To the maximum extent permitted by law, TenantPlus shall not be liable for indirect, incidental, or consequential damages. Our total liability shall not exceed the amount paid by you to us in the previous 12 months.
        </p>

        <h2>11. Termination</h2>
        <p>
          You may stop using the Platform at any time. We may suspend or terminate your access for misuse, legal requirements, or operational reasons. Your landlord may also request that your access be limited or revoked.
        </p>

        <h2>12. Changes to These Terms</h2>
        <p>
          We may update these Terms from time to time. Continued use of the Platform after changes constitutes acceptance.
        </p>

        <h2>13. Governing Law</h2>
        <p>
          These Terms are governed by the laws of the Republic of Kenya.
        </p>
      </Content>
      <FooterLinks>
        <Link to="/tenant/privacy">Privacy Policy (Tenant Portal)</Link>
      </FooterLinks>
    </Container>
  );
};

export default TenantTermsOfUse;
